/**
 * Static data for Solutions sub-pages (industries).
 *
 * Proof metrics are ANONYMIZED reference outcomes — never tie a name to them.
 * Solution Packs map to features/solution-packs.md. No fabricated logos/testimonials.
 */

import {
  HeartPulse,
  Landmark,
  Users,
  ServerCog,
  Umbrella,
  Scale,
  RadioTower,
  Factory,
  GraduationCap,
  Receipt,
  type LucideIcon,
} from "lucide-react";

export interface ProofPoint {
  metric: string;
  label: string;
}

export interface RelatedLink {
  /** page id used by onNavigate */
  page: string;
  label: string;
  desc: string;
}

export interface IndustrySolution {
  /** route slug, e.g. "healthcare" → /solutions/healthcare */
  slug: string;
  /** page id, e.g. "solutions-healthcare" */
  pageId: string;
  icon: LucideIcon;
  accent: string; // tailwind color name used for the soft icon chip
  name: string;
  eyebrow: string;
  /** contrast headline — "Not X. Y." craft */
  headline: string;
  subhead: string;
  problem: string;
  solveHeadline: string;
  solveBody: string;
  automate: string[];
  keepHuman: string;
  proof: ProofPoint[];
  /** heading over the proof band; defaults to the in-production line */
  proofHeadline?: string;
  proofNote: string;
  pack: {
    name: string;
    body: string;
  };
  related: RelatedLink[];
  metaTitle: string;
  metaDescription: string;
}

export const industries: IndustrySolution[] = [
  {
    slug: "healthcare",
    pageId: "solutions-healthcare",
    icon: HeartPulse,
    accent: "rose",
    name: "Healthcare",
    eyebrow: "Healthcare",
    headline: "Chart audit at 11× the pace. Clinicians keep the final say.",
    subhead:
      "From discharge summary to admission record, and from a recorded session to a structured note — every field traceable to its source, and nothing signed until a clinician says so.",
    problem:
      "Charts, prior authorisations, and clinical notes pile up faster than people can process them. Errors get caught too late, and adding headcount only moves the bottleneck.",
    solveHeadline: "AI that operates inside clinical reality, not around it.",
    solveBody:
      "Clinical work has zero tolerance for error and zero appetite for opacity. TurfAI pairs an agentic workflow engine with configurable human-in-the-loop gates at every critical decision point, so automation handles the volume while clinicians stay accountable for judgment.",
    automate: [
      "Chart review and audit — routed by chart type, with a confidence score and full reasoning",
      "Prior authorisation and claims — checked against the payer's requirements before submission",
      "Clinical documentation — discharge summaries and notes, confirmed by the clinician",
      "Operational reporting and compliance monitoring an auditor can follow",
      "Patient intake — discharge summaries turned into admission records, with blocking alerts for missing prior authorisation or physician IDs",
      "Visit notes mapped to the assessment form clinicians file, and therapy sessions turned into SOAP notes",
      "Patient communication triage — enquiries routed, exceptions escalated",
    ],
    keepHuman:
      "Diagnosis, treatment decisions, and anything where clinical judgement is non-negotiable. The human-in-the-loop gates are configurable — your clinical team defines where the AI stops and a person confirms.",
    proof: [
      { metric: "11×", label: "clinical throughput" },
      { metric: "99.4%", label: "chart-audit accuracy" },
      { metric: "0", label: "headcount added" },
    ],
    proofNote:
      "Reference outcome from a healthcare provider running chart review on TurfAI — live in production.",
    pack: {
      name: "Healthcare Domain Pack",
      body: "Pre-built workflow templates for clinical operations — chart audit, prior authorisation, and documentation — each a deployment pattern that has already run somewhere, with configurable HITL at every critical step.",
    },
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Turn clinical documents into structured data." },
      { page: "platform-rag", label: "RAG & Knowledge", desc: "Grounded answers over policies and records." },
    ],
    metaTitle: "Healthcare — Chart audit, prior auth & clinical notes | TurfAI",
    metaDescription:
      "TurfAI automates clinical document workflows — chart audit, prior auth, and notes — with human-in-the-loop gates. A reference deployment hit 11× throughput at 99.4% chart-audit accuracy.",
  },
  {
    slug: "bfsi",
    pageId: "solutions-bfsi",
    icon: Landmark,
    accent: "indigo",
    name: "BFSI",
    eyebrow: "Banking & Financial Services",
    headline: "Loan files at 24× the pace. Credit officers still sign off.",
    subhead:
      "Compliance-heavy work doesn't have to mean people-heavy work. TurfAI automates the document and decision steps without giving up the audit trail regulators expect.",
    problem:
      "Loan files, KYC packs, and credit memos are buried in unstructured documents and manual hand-offs. Throughput — and per-document cost — is capped by how fast people can read, key in, and route.",
    solveHeadline: "Speed without losing the paper trail.",
    solveBody:
      "TurfAI extracts, classifies, and routes documents through agentic workflows, with Data Shield tokenising PII before anything reaches a model and an immutable audit log on every step. People review the exceptions; the platform handles the volume.",
    automate: [
      "Loan document processing — B2B and retail ingestion, extraction, and validation, with per-field confidence and a review queue",
      "KYC and AML — document processing and flag escalation, with the trail an examiner can follow",
      "Credit memo generation from structured financial data",
      "Compliance squad — KYC validation, risk scoring and a compliance report, assembled by specialist agents",
      "Invoice and receipt processing — line items extracted, validated against the PO and routed for approval",
      "Trade-finance documents — invoices, bills of lading and packing lists merged into one docket, customs fields filled",
      "Compliance documentation with timestamp integrity built in",
      "Customer communication triage — enquiries routed, exceptions escalated with context",
    ],
    keepHuman:
      "Credit approval decisions, regulatory exceptions, and fraud escalation. The AI surfaces the information and the risk signals — the decision stays with your team.",
    proof: [
      { metric: "24×", label: "employee productivity" },
      { metric: "100%", label: "of steps audit-logged" },
      { metric: "Per-field", label: "confidence, with a review queue" },
    ],
    proofNote:
      "Reference outcome from a B2B lender automating document-heavy workflows on TurfAI.",
    pack: {
      name: "BFSI Domain Pack",
      body: "Pre-built workflow templates for financial document processing — loan ingestion, KYC extraction, and credit memos. Audit-trail-first, with configurable human-in-the-loop at regulated decision points.",
    },
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Document & decision agents for loans and KYC." },
      { page: "platform-squads", label: "Agent Squads", desc: "A compliance squad that validates KYC, scores risk and writes the report." },
    ],
    metaTitle: "BFSI — Loan documents, KYC & credit memos | TurfAI",
    metaDescription:
      "TurfAI automates loan document processing, KYC/AML, and credit memos with agentic workflows, Data Shield PII protection, and full audit logs. A reference lender reached 24× productivity.",
  },
  {
    slug: "insurance",
    pageId: "solutions-insurance",
    icon: Umbrella,
    accent: "brand",
    name: "Insurance",
    eyebrow: "Insurance",
    headline: "Claims triaged in minutes. Claims officers still sign off.",
    subhead:
      "Seven life-insurance operations on one governed platform — claims intake, settlement, underwriting, renewals, billing, fraud and complaints — with an officer signing off wherever money or a decision moves.",
    problem:
      "Claims arrive as emails, forms and photos; submissions arrive as broker packs. Each one is read, keyed and routed by hand, so first notice to acknowledgement takes days and complaint deadlines live in spreadsheets.",
    solveHeadline: "From first notice to settlement offer, on one auditable rail.",
    solveBody:
      "The Insurance Domain Pack ships 14 agents, 7 workflows and 3 agent squads covering claims, application underwriting, policy issuance, renewals, fraud indicators and complaints. Data Shield tokenises policyholder data before any model sees it, and every step lands in the audit log.",
    automate: [
      "Claims triage — four intake agents capture, screen, validate and triage every claim from forms, emails, call transcripts and full death-claim document sets",
      "Claims settlement — a squad gathers evidence, checks coverage, values the claim and drafts the offer for a claims officer to sign",
      "Underwriting — applications scored and routed, with a manual review step for referrals; a raw broker submission becomes an argued recommendation memo",
      "Policy issuance — approved applications become issued policies and documents",
      "Policy renewal — a 90-day pipeline: account review, coverage gaps, recommendation and broker communication",
      "Fraud indicators — suspicious claims flagged for SIU review without slowing legitimate ones",
      "Complaint handling — captured, classified and tracked against regulatory deadlines on a live clock",
      "Settlement payments held until bank verification passes",
    ],
    keepHuman:
      "Settlement offers, coverage denials, underwriting decisions and SIU referrals. The squads assemble the evidence and the draft; a claims officer or underwriter makes the call.",
    proof: [
      { metric: "<20 min", label: "claim triage, vs. ~2 days manual" },
      { metric: "14", label: "insurance agents, ready to run" },
      { metric: "3", label: "squads: settlement, underwriting, broker support" },
    ],
    proofHeadline: "What the pack ships with.",
    proofNote:
      "Claim-triage time from the Insurance Pack's claims workflow; agent and squad counts are the pack's contents.",
    pack: {
      name: "Insurance Domain Pack",
      body: "Claims intake and triage, application underwriting, manual underwriting review, policy issuance, renewal reminders, complaint handling and fraud indicator workflows, plus Claims Settlement, Underwriting and Broker Support squads. Install it as-is or fork any agent, workflow or squad.",
    },
    related: [
      { page: "platform-squads", label: "Agent Squads", desc: "Five specialists that draft a settlement offer together." },
      { page: "platform-governance", label: "Governance & Controls", desc: "Audit trails and deadlines a regulator can follow." },
    ],
    metaTitle: "Insurance — Claims, underwriting & complaints | TurfAI",
    metaDescription:
      "TurfAI's Insurance Domain Pack automates claims triage, settlement, underwriting, renewals, fraud indicators and complaints with agent squads and human sign-off. Claim triage in under 20 minutes.",
  },
  {
    slug: "hr-tech",
    pageId: "solutions-hr-tech",
    icon: Users,
    accent: "teal",
    name: "HR Tech",
    eyebrow: "HR & People Operations",
    headline: "Policy answers in seconds. HR handles what needs a person.",
    subhead:
      "Hiring, onboarding and employee support on one platform — StageOne assesses candidates, DayOne onboards them and Ask HR answers every policy question with a citation.",
    problem:
      "Recruiters screen the same resumes, HR fields the same policy questions, and new hires wait on manual paperwork. The work scales linearly with headcount.",
    solveHeadline: "Automate the repetitive. Keep the human in the room.",
    solveBody:
      "TurfAI runs the screening, support, and onboarding workflows end to end, grounded in your own policies and systems. StageOne, Ask HR, and DayOne are named accelerators built on exactly this engine.",
    automate: [
      "Support ticket triage — known answers at first touch, everything else routed with context",
      "Hiring orchestration — job descriptions, screening, scheduling, and feedback as one workflow",
      "Resume screening against the job description, with an AI summary for the recruiter and an interview kit generated per role",
      "Pre-boarding welcome emails and scheduled performance-review reminders, sent on time without a spreadsheet",
      "Onboarding — tasks and employee communication through the first weeks",
      "Benefits and leave queries, escalated where a policy needs interpreting",
      "Policy question answering over your own policy corpus, with citations",
    ],
    keepHuman:
      "Offer decisions, termination conversations, and disciplinary matters — anywhere nuance, empathy, and judgement are non-negotiable. The AI prepares the information; a person makes the call.",
    proof: [
      { metric: "10×", label: "HR workflow efficiency" },
      { metric: "3–4 weeks", label: "typical time to production" },
      { metric: "24/7", label: "self-service coverage" },
    ],
    proofNote:
      "Reference outcome from an HR services provider running employee support on TurfAI.",
    pack: {
      name: "HR Tech Domain Pack",
      body: "The HR Tech Domain Pack ships a resume screener, an interview kit generator and ready-made hiring, onboarding and policy Q&A workflows, and connects to ATS platforms such as JobAdder. StageOne for AI video screening, Ask HR for grounded employee support, and DayOne for guided onboarding are ready-to-deploy accelerators built on it.",
    },
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Screening and support, grounded in your policies." },
      { page: "platform-rag", label: "RAG & Knowledge", desc: "Employee answers with citations." },
    ],
    metaTitle: "HR Tech — Hiring, support & onboarding | TurfAI",
    metaDescription:
      "TurfAI automates HR workflows — screening, employee support, and onboarding — with StageOne, Ask HR, and DayOne. A reference provider reached 10× workflow efficiency.",
  },
  {
    slug: "legal",
    pageId: "solutions-legal",
    icon: Scale,
    accent: "brand",
    name: "Legal",
    eyebrow: "Legal",
    headline: "First drafts in the firm's own structure. Lawyers finish them.",
    subhead:
      "Upload a petition and get a structured reply — objections, para-by-para response, prayer — plus NDAs, case timelines and reports, drafted on TurfAI and reviewed by counsel.",
    problem:
      "First drafts are repetitive and slow: the same structure, the same objections, rewritten by hand for every matter. Associates spend their hours formatting instead of arguing.",
    solveHeadline: "Every drafting task is a governed workflow.",
    solveBody:
      "Petition replies, NDAs, timelines, medical and insurance reports and translations each run as their own TurfAI workflow, using the firm's own prompts — tested against past drafts before they go live. Chat with your files answers from the matter's own documents.",
    automate: [
      "Structured replies to petitions and appeals — preliminary objections, para-by-para reply and prayer",
      "NDA and contract first drafts in the firm's house style",
      "Case timelines built from the documents on file",
      "Medical and insurance reports for litigation",
      "Translation of filings and evidence",
      "Chat with your files — answers grounded in the matter's documents",
    ],
    keepHuman:
      "Every draft is edited and approved by a lawyer. Nothing is filed by the system, and legal judgement never leaves counsel.",
    proof: [
      { metric: "50%", label: "faster first drafts" },
      { metric: "7", label: "drafting workflows on one platform" },
      { metric: "100%", label: "of drafts approved by a lawyer" },
    ],
    proofHeadline: "Drafting, without the drudgery.",
    proofNote: "Lawyers spend their hours on argument, not formatting.",
    pack: {
      name: "Legal workflows",
      body: "Petition replies, NDAs, timelines, reports and translation, each a TurfAI workflow driven by the firm's own prompts — versioned, tested and auditable.",
    },
    related: [
      { page: "platform-rag", label: "RAG & Knowledge", desc: "Answers grounded in the matter's documents." },
      { page: "platform-governance", label: "Governance & Controls", desc: "Prompt review and audit for every draft." },
    ],
    metaTitle: "Legal — Petition replies, NDAs & case timelines | TurfAI",
    metaDescription:
      "TurfAI drafts petition replies, NDAs, case timelines and litigation reports in your firm's own structure, with every draft reviewed and approved by a lawyer.",
  },
  {
    slug: "telecom",
    pageId: "solutions-telecom",
    icon: RadioTower,
    accent: "brand",
    name: "Telecom",
    eyebrow: "Telecom",
    headline: "SMS templates checked against the rules before they're rejected.",
    subhead:
      "For aggregators and registrars: every template is checked for category, variables, length and opt-out wording, scored 0–100 and returned with the clause it breaks — a registrar still makes the call.",
    problem:
      "Commercial SMS templates are rejected for the wrong category, misused variables, character limits or missing opt-out wording. Registrars review every one by hand, in English, Hindi and Hinglish.",
    solveHeadline: "Compliance checks in seconds, with the clause cited.",
    solveBody:
      "A TurfAI workflow classifies each template's intent and category, deterministic checks handle variables, length and required wording, and every verdict cites the regulation behind it. Data Shield tokenises brand names, sender IDs and recipient data before the model sees them.",
    automate: [
      "Instant pre-submission checks for businesses",
      "First-level review for registrars, with Approve / Reject / Return recommendations",
      "Suggested corrected templates",
      "A regulation clause cited on every verdict",
      "English, Hindi and Hinglish content",
      "Bulk checks over an API, with signed webhooks",
    ],
    keepHuman:
      "The registrar approves, rejects or returns every template, and their corrections feed back into the next decision.",
    proof: [
      { metric: "99.8%", label: "category accuracy" },
      { metric: "131/min", label: "templates checked" },
      { metric: "0", label: "compliant templates wrongly failed" },
    ],
    proofHeadline: "A week's queue, cleared in an afternoon.",
    proofNote: "Measured across 5,000 templates.",
    pack: {
      name: "Telecom compliance workflows",
      body: "Template classification, rule checks, scoring and registrar review, with Data Shield on sender and recipient data and a feedback loop from every correction.",
    },
    related: [
      { page: "platform-governance", label: "Governance & Controls", desc: "Every verdict audited and explainable." },
      { page: "security", label: "Security & Trust", desc: "Data Shield on sender and recipient data." },
    ],
    metaTitle: "Telecom — SMS template compliance | TurfAI",
    metaDescription:
      "TurfAI checks SMS templates against telecom rules — category, variables, length and opt-out wording — scores each one and cites the clause, with registrars making the final call.",
  },
  {
    slug: "manufacturing",
    pageId: "solutions-manufacturing",
    icon: Factory,
    accent: "brand",
    name: "Manufacturing",
    eyebrow: "Manufacturing",
    headline: "From handwritten shift logs to the training that fixes rejects.",
    subhead:
      "Production managers upload shift logs — scanned and handwritten included. TurfAI reads them, explains what drove rejects and downtime, and assigns the right training to the right operator.",
    problem:
      "Parts are rejected in nearly every batch, but nobody can say which operator needs which training, deliver it, or check it worked. Shift logs sit on paper.",
    solveHeadline: "Read the floor. Fix the cause.",
    solveBody:
      "Four TurfAI workflows extract shift logs, analyse rejects and downtime, estimate materials and sequence orders. Cells TurfAI isn't sure about are flagged for the manager, and approving a report triggers training for the operators who need it.",
    automate: [
      "Handwritten shift logs turned into clean rows",
      "Plain-language shift reports: rejects, downtime, machines and what to fix first",
      "Training assigned to the operators who need it, delivered to their phones, with progress tracked",
      "Raw-material estimates for new orders from similar past orders",
      "Order sequencing with a reason for every position",
    ],
    keepHuman:
      "The production manager approves every report before any training goes out, and checks every cell TurfAI flags as uncertain.",
    proof: [
      { metric: "Same day", label: "from shift log to training" },
      { metric: "4", label: "TurfAI workflows on the floor" },
      { metric: "100%", label: "of reports approved by a manager" },
    ],
    proofHeadline: "Rejects traced to their cause.",
    proofNote: "Every reject traced to the operator, machine and step behind it.",
    pack: {
      name: "Manufacturing workflows",
      body: "Log extraction, log analysis, material estimation and order prioritisation, wired to training delivery and email.",
    },
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Agents that read logs and explain them." },
      { page: "platform-edge-ai", label: "Edge AI", desc: "Run the same workflows on the factory floor." },
    ],
    metaTitle: "Manufacturing — Shift logs, rejects & training | TurfAI",
    metaDescription:
      "TurfAI reads handwritten shift logs, explains rejects and downtime, assigns targeted operator training, estimates materials and sequences orders — with managers approving every report.",
  },
  {
    slug: "education",
    pageId: "solutions-education",
    icon: GraduationCap,
    accent: "brand",
    name: "Education",
    eyebrow: "Education",
    headline: "Admissions decisions you can explain years later.",
    subhead:
      "For universities and institutes: deterministic rules decide, AI assists, officials approve — with a decision record behind every admission.",
    problem:
      "Admission decisions have to be correct, fair and defensible long after the cycle closes — across eligibility, cut-offs, merit, reservation and seat allocation, for thousands of applicants.",
    solveHeadline: "Rules decide. AI assists. Officials approve.",
    solveBody:
      "Admission policy lives as versioned rule packs and runs without AI in the decision path. TurfAI reads and classifies application documents with a confidence score per field, and every AI-proposed action passes registry, schema, identity and policy checks before it runs.",
    automate: [
      "Eligibility, cut-off, merit, tie-break and reservation rules",
      "Document classification and field extraction, with low-confidence fields sent to a person",
      "Guardrails on every AI action before it runs",
      "Replay of any past admission cycle from its decision record",
      "Reviewer screens for caseload, cut-offs, merit, allocation and offers",
    ],
    keepHuman:
      "Officials approve every cut-off under a maker-checker rule — the person who prepares a decision can't also approve it.",
    proof: [
      { metric: "100%", label: "of decisions replayable" },
      { metric: "Hours", label: "to approve cut-offs, not days" },
      { metric: "0", label: "AI in the decision path" },
    ],
    proofHeadline: "Defensible by design.",
    proofNote: "An audit question answered in minutes, not weeks.",
    pack: {
      name: "Education & Learning Domain Pack",
      body: "Course content generation, adaptive assessment and training ROI, alongside admissions rule packs and document workflows.",
    },
    related: [
      { page: "learning", label: "Learning tools", desc: "SkillUp, DataLab and FishBone." },
      { page: "platform-governance", label: "Governance & Controls", desc: "A record behind every decision." },
    ],
    metaTitle: "Education — Admissions you can explain | TurfAI",
    metaDescription:
      "TurfAI runs university admissions with versioned rule packs, AI-assisted document reading and maker-checker approvals — every decision replayable from its record.",
  },
  {
    slug: "invoice-processing",
    pageId: "solutions-invoice-processing",
    icon: Receipt,
    accent: "brand",
    name: "Invoice Processing",
    eyebrow: "Finance · Accounts payable",
    headline: "Invoices read, checked and ready for your ERP.",
    subhead:
      "Upload or forward invoices — digital, scanned or forty pages long. TurfAI reads every field, checks the totals and tax IDs, and sends clean invoices straight through; the rest wait for one click.",
    problem:
      "Finance teams key in invoice data by hand, check totals and tax IDs line by line, and post to the ERP — across digital, scanned and multi-page invoices from hundreds of suppliers.",
    solveHeadline: "Straight-through for the clean ones. One click for the rest.",
    solveBody:
      "Every step of document understanding runs in TurfAI workflows. Invoices that arrive by email flow from Gmail into a Drive folder per vendor, then into extraction, and signed webhooks keep the review queue live.",
    automate: [
      "Digital, scanned or image-only PDFs, from one page to forty and more",
      "Show-me-where review — each value highlighted on the page",
      "Checks built in: totals that add up, and GSTIN, VAT, TRN, HSN and QR validation",
      "Straight-through approval for invoices that pass every check",
      "Supplier and customer masters, matched automatically",
      "ERP export to Odoo, Zoho Books, SAP, Oracle and JD Edwards",
    ],
    keepHuman:
      "Anything that fails a check waits for one click — approve, correct or reject — and every change is logged.",
    proof: [
      { metric: "85%", label: "approved straight through" },
      { metric: "~3¢", label: "per scanned invoice" },
      { metric: "40+", label: "pages per invoice, no problem" },
    ],
    proofHeadline: "Finance touches only the exceptions.",
    proofNote: "Measured on the live pipeline.",
    pack: {
      name: "Invoice Processing",
      body: "Batch upload, email intake, extraction, validation, supplier masters and ERP export — all running on TurfAI workflows.",
    },
    related: [
      { page: "solutions-bfsi", label: "BFSI", desc: "Invoices inside wider financial workflows." },
      { page: "platform-integrations", label: "Integrations & MCP", desc: "Gmail, Drive and your ERP." },
    ],
    metaTitle: "Invoice Processing — Accounts payable on autopilot | TurfAI",
    metaDescription:
      "TurfAI reads digital and scanned invoices, validates totals and tax IDs, approves clean invoices straight through and exports to your ERP — about three US cents per scanned invoice.",
  },
  {
    slug: "it-ops",
    pageId: "solutions-it-ops",
    icon: ServerCog,
    accent: "purple",
    name: "IT Ops",
    eyebrow: "IT Operations & Support",
    headline: "Routine incidents close themselves. Engineers take the rest.",
    subhead:
      "Alert correlation, root cause analysis, and auto-remediation for defined playbooks — with a human in the loop for every decision outside them.",
    problem:
      "Monitoring stacks fire duplicate alerts, root cause takes a war room, and routine fixes — a full disk, a hung service — still page an engineer. Every escalation starts from scratch.",
    solveHeadline: "Detect, diagnose, and remediate — before anyone is paged.",
    solveBody:
      "TurfAI correlates alerts across the monitoring stack, produces explainable root-cause analysis an engineer can check, and runs defined remediations without a person — escalating everything else with full context, on an auditable execution layer.",
    automate: [
      "Alert correlation — ingestion, deduplication, and correlation, so one incident is one incident",
      "Root cause analysis an engineer can check, not a verdict they have to trust",
      "Auto-remediation for defined categories — disk clean-up, service restart, config rollback",
      "Escalation with context — what happened, what was tried, what it recommends",
      "Change requests analysed for risk and impact before they reach the CAB",
      "SLA compliance monitored continuously, with breaches flagged before they happen",
      "Operational reporting — incident summaries and morning reports generated, not assembled",
    ],
    keepHuman:
      "Major incident declaration, customer communication, changes to production outside a defined playbook, and security breach response. Every guardrail is configurable — your team decides where the AI stops.",
    proof: [
      { metric: "RCA", label: "explainable, engineer-checkable" },
      { metric: "HITL", label: "outside every defined playbook" },
      { metric: "100%", label: "of remediations audit-logged" },
    ],
    proofHeadline: "What the pack does",
    proofNote:
      "Capability fit for IT operations on TurfAI — framed by platform controls, not fabricated metrics.",
    pack: {
      name: "IT Ops Domain Pack",
      body: "Ships with an incident summariser, a change-request analyser and an SLA compliance monitor, plus an ITSM squad that triages, investigates and drafts the resolution — with configurable human-in-the-loop for every decision outside a defined playbook.",
    },
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Multi-agent pipelines: correlate, diagnose, remediate." },
      { page: "platform-integrations", label: "Integrations & MCP", desc: "Connect the monitoring and ITSM stack." },
    ],
    metaTitle: "IT Ops — Alert correlation & auto-remediation | TurfAI",
    metaDescription:
      "TurfAI automates IT operations — alert correlation, root cause analysis, auto-remediation, and escalation with context — with human-in-the-loop outside defined playbooks.",
  },
];

// One calm enterprise system: brand blue for most chips, teal accent reserved for
// the agentic/AI building blocks. Legacy per-hue names all map onto these two.
// ponytail: keep the `accent` field on the data so individual items can opt into teal.
const softChip: Record<string, string> = {
  brand: "bg-indigo-tint-20 text-royal-indigo",
  accent: "bg-indigo-tint-20 text-royal-indigo",
  // legacy hue names → brand, except the agentic ones → teal accent
  rose: "bg-indigo-tint-20 text-royal-indigo",
  indigo: "bg-indigo-tint-20 text-royal-indigo",
  blue: "bg-indigo-tint-20 text-royal-indigo",
  teal: "bg-indigo-tint-20 text-royal-indigo",
  purple: "bg-indigo-tint-20 text-royal-indigo",
};

export function chipClass(accent: string): string {
  return softChip[accent] ?? softChip.brand;
}

export function getIndustry(slug: string): IndustrySolution | undefined {
  return industries.find((i) => i.slug === slug);
}

